import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationStatus } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { assertNever } from "@redotech/util/type";
import { browserIsMac } from "@redotech/web-util/browser";
import { useMemo } from "react";
import {
  useMarkConversationUnread,
  useUpdateConversationStatus,
} from "./use-conversation-mutations";

export const conversationActionId = [
  "markOpen",
  "markInProgress",
  "markUnread",
  "archive",
  "close",
  "reopen",
] as const;
export type ConversationActionId = (typeof conversationActionId)[number];

export interface ActionMenuItem {
  id: ConversationActionId;
  text: string;
  tooltip?: string;
  disabled?: boolean;
}

export interface CloseReopenAction {
  id: Extract<ConversationActionId, "close" | "reopen">;
  text: string;
  tooltip: string;
}

export interface UseConversationActionToolbarResult {
  closeReopenAction: CloseReopenAction;
  menuItems: ActionMenuItem[];
  isLoading: boolean;
  canCloseConversation: boolean;
  handleCloseReopen: () => void;
  handleMenuAction: (actionId: ConversationActionId) => void;
}

export function useConversationActionToolbar(
  conversationId: ConversationId,
  status: ConversationStatus,
): UseConversationActionToolbarResult {
  const updateStatus = useUpdateConversationStatus();
  const markAsUnread = useMarkConversationUnread();

  // Permission checks
  const canCloseConversation = useHasPermission(
    SupportPermissions.CLOSE_CONVERSATION,
  );
  const canArchiveConversation = useHasPermission(
    SupportPermissions.ARCHIVE_CONVERSATION,
  );

  const isMac = browserIsMac();
  const isClosed = status === "CLOSED";
  const isInProgress = status === "IN_PROGRESS";
  const isArchived = status === "ARCHIVED";

  const closeReopenAction = useMemo((): CloseReopenAction => {
    if (isClosed) {
      return {
        id: "reopen",
        text: "Reopen",
        tooltip: isMac ? "Option+R" : "Alt+R",
      };
    }
    return {
      id: "close",
      text: "Close",
      tooltip: isMac ? "Option+C" : "Alt+C",
    };
  }, [isClosed, isMac]);

  const menuItems = useMemo((): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];

    // Mark as open (when in progress and has permission)
    if (isInProgress && canCloseConversation) {
      items.push({
        id: "markOpen",
        text: "Mark as open",
        tooltip: isMac ? "Option+R" : "Alt+R",
      });
    }

    // Mark in progress (when not already in progress, not closed, and has permission)
    if (!isInProgress && !isClosed && canCloseConversation) {
      items.push({
        id: "markInProgress",
        text: "Mark in progress",
        tooltip: isMac ? "Option+P" : "Alt+P",
      });
    }

    // Mark unread (no permission required - UI state management)
    items.push({ id: "markUnread", text: "Mark unread" });

    // Archive (when not already archived and has permission)
    if (!isArchived && canArchiveConversation) {
      items.push({ id: "archive", text: "Archive" });
    }

    return items;
  }, [
    isInProgress,
    isClosed,
    isArchived,
    isMac,
    canCloseConversation,
    canArchiveConversation,
  ]);

  const handleCloseReopen = useHandler(() => {
    if (isClosed) {
      updateStatus(conversationId, "OPEN");
    } else {
      updateStatus(conversationId, "CLOSED");
    }
  });

  const handleMenuAction = useHandler((actionId: ConversationActionId) => {
    switch (actionId) {
      case "markOpen":
        updateStatus(conversationId, "OPEN");
        break;
      case "markInProgress":
        updateStatus(conversationId, "IN_PROGRESS");
        break;
      case "markUnread":
        markAsUnread(conversationId);
        break;
      case "archive":
        updateStatus(conversationId, "ARCHIVED");
        break;
      case "close":
        updateStatus(conversationId, "CLOSED");
        break;
      case "reopen":
        updateStatus(conversationId, "OPEN");
        break;
      default:
        assertNever(actionId);
    }
  });

  return {
    closeReopenAction,
    menuItems,
    isLoading: false,
    canCloseConversation,
    handleCloseReopen,
    handleMenuAction,
  };
}
